import { ComponentProps } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import Modal from '~/shared/components/Modal';
import { Volume } from '~/shared/types/api';
import WarningIcon from '~/shared/assets/icons/warning.svg';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/Buttons';
import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';

import { baseUrl } from '../FleetManagerPage/helpers';

export default function DeleteModal({
  showModal,
  onClose,
  volume: { volumeName, volumeId },
}: Omit<ComponentProps<typeof Modal>, 'children'> & { volume: Volume }) {
  const api = useXFetch();
  const navigate = useNavigate();

  const { mutateAsync: deleteVolume } = useMutation({
    mutationKey: ['delete-volume', volumeId],
    mutationFn: async () => {
      navigate('/fleet-manager');
      await api<unknown>(`${baseUrl}/${volumeId}`, {
        method: 'DELETE',
      });
    },
    onSettled(_data, error) {
      if (error) {
        toaster({
          variant: 'error',
          title: 'Deletion Error',
          message: `Volume ${volumeId} has failed to delete.`,
          toastId: volumeId,
        });
      }
    },
  });

  return (
    <Modal showModal={showModal} size="medium" onClose={onClose}>
      <div className="flex flex-col w-full items-center">
        <WarningIcon className="w-[32.3px] aspect-square fill-red mb-[12px]" />
        <Typography variant="H3" fontWeight={600} className="mb-[10px]">
          Are you sure you want to delete this volume?
        </Typography>
        <Typography variant="p3" className="mb-[24px] max-w-[536px] text-center">
          Deleting a volume isn&apos;t reversible, please ensure there isn&apos;t data you might
          require on this volume
        </Typography>

        <div className="flex gap-[24px] mb-[20px] items-center">
          {volumeName ? (
            <div className="flex gap-[16px] h-[48px] items-center">
              <Typography variant="p3" color="text-grey">
                Name
              </Typography>
              <Typography variant="p3" color="text-primary-400">
                {volumeName}
              </Typography>
            </div>
          ) : null}
          <div className="flex gap-[16px] h-[48px] items-center">
            <Typography variant="p3" color="text-grey">
              Volume ID
            </Typography>
            <Typography variant="p3" color="text-primary-400">
              {volumeId}
            </Typography>
          </div>
        </div>

        <div className="flex gap-[4px]">
          <Button variant="secondary" className="w-[178px] h-[44px]" onClick={() => deleteVolume()}>
            <DeleteIcon className="w-6 aspect-square fill-primary-800" />
            <Typography variant="p3" color="text-primary-900">
              Delete volume
            </Typography>
          </Button>
          <Button className="w-[152px]" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
